import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardBody, ButtonGroup, Button, Spinner } from 'reactstrap';
import { FaChartLine, FaBoxOpen, FaCalendarAlt, FaMoneyBillWave, FaFileInvoice, FaFileInvoiceDollar, FaUndoAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { saleActions } from '../../sagas/saleSlice';
import ReglementWidget from '../Reglement/ReglementWidget';
import { unitesActions } from '../../sagas/unitesSlice';

const Dashboard = () => {
  const [dateRange, setDateRange] = useState('today');
  const dispatch = useDispatch();
  const {
    totalVentes,
    totalVentesDirect,
    totalAchats,
    totalFactureImpayees,
    totalAchatsImpayees,
    invoiceNotPayed,
    totalPurchases,
    purchaseNotPayed,
    purchasePayed,
    totalPOSSales,
    totalSalesReturns,
    totalPurchaseReturns,
    isLoading
  } = useSelector(state => state.sales);

  useEffect(() => {
    dispatch(unitesActions.list()); // Fetch categories on component mount
  }, []);
  useEffect(() => {
    dispatch(saleActions.getSalesData({ dateRange }));
  }, [dispatch, dateRange]);

  const { t } = useTranslation();
  const formatPrice = (amount) => {
    const formattedPrice = new Intl.NumberFormat('fr-MA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
    return `${formattedPrice} DH`;
  };

  const cardStyle = {
    height: '200px', // Adjust the height value as needed
    borderRadius: '10px',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
  };

  const numberStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
  };

  return (
    <Container fluid>
      {/* Date Range Selector */}
      <Row className="mb-4" >
        <Col>
          <Card className="shadow-sm" >
            <CardBody className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 d-flex align-items-center">
                <FaCalendarAlt className="text-primary me-2" />
                {t('dashboard.reports')}
              </h4>
              <ButtonGroup>
                <Button
                  color={dateRange === 'today' ? 'primary' : 'light'}
                  onClick={() => {
                    setDateRange('today');
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.today')
                  )}
                </Button>
                <Button
                  color={dateRange === 'week' ? 'primary' : 'light'}
                  onClick={() => setDateRange('week')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.week')
                  )}
                </Button>
                <Button
                  color={dateRange === 'month' ? 'primary' : 'light'}
                  onClick={() => setDateRange('month')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.month')
                  )}
                </Button>
                <Button
                  color={dateRange === 'year' ? 'primary' : 'light'}
                  onClick={() => setDateRange('year')}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner size="sm" className="me-1" />
                  ) : (
                    t('dashboard.year')
                  )}
                </Button>
              </ButtonGroup>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Invoices Summary Cards */}

      <Row className="mb-4">
        <Col md="3">
          <Card className="shadow-sm " style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaFileInvoiceDollar className="text-primary me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalAllSales')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(totalVentes)}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="shadow-sm" style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaMoneyBillWave className="text-success me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalDirectSales')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(totalVentesDirect)}</h3>
            </CardBody>
          </Card>
        </Col>


        <Col md="3">
          <Card className="shadow-sm" style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaFileInvoice className="text-success me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.customerInvoiceNotPayed')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(totalFactureImpayees)}</h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="shadow-sm" style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaUndoAlt className="text-warning me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalSalesReturns')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(0)}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md="3">
          <Card className="shadow-sm " style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaChartLine className="text-primary me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.numbreSupplierInvoices')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(totalAchats)}</h3>
            </CardBody>
          </Card>
        </Col>

        <Col md="3">
          <Card className="shadow-sm" style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaFileInvoice className="text-info me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.supplierInvoiceNotPayed')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(totalAchatsImpayees)} </h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="shadow-sm " style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaFileInvoiceDollar className="text-success me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalExpenses')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(0)} </h3>
            </CardBody>
          </Card>
        </Col>
        <Col md="3">
          <Card className="shadow-sm" style={cardStyle}>
            <CardBody>
              <div className="d-flex align-items-center mb-3">
                <FaUndoAlt className="text-danger me-2" size={20} />
                <h5 className="mb-0">{t('dashboard.totalPurchaseReturns')}</h5>
              </div>
              <h3 className="mb-0" style={numberStyle}>{formatPrice(0)}</h3>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row className="mb-4">
        <Col>
          <h5 className="mb-3">
            <FaMoneyBillWave className="me-2 text-primary" />
            {t('reglement.gestionReglements')}
          </h5>
          <ReglementWidget />
        </Col>
      </Row>


    </Container >
  );
};

export default Dashboard;